import * as BABYLON from "babylonjs"
import { Input } from "./input"
import { Motion } from "./types"

const Direction = Object.freeze({
	NONE: Symbol("none"),
	LEFT: Symbol("left"),
	RIGHT: Symbol("right"),
})

export class Player {
	static engine: BABYLON.Engine
	static scene: BABYLON.Scene
	static skeleton: BABYLON.Skeleton
	static direction = Direction.NONE
	static root: BABYLON.Nullable<BABYLON.AbstractMesh>
	static mesh: BABYLON.Nullable<BABYLON.AbstractMesh>
	static fabric: BABYLON.PBRMaterial
	static hems: BABYLON.PBRMaterial
	static absoluteRotation = Math.PI / 2
	static velocity = BABYLON.Vector3.Zero()
	static speed = {
		rotate: 0.0005,
		walk: 0.00004,
		run: 0.0001,
		slow: 0.000018,
		back: 0.00004,
	}
	static isMoving = false
	static interval = 20
	static viewingRotation = 0
	static smoothingFactor = 5
	static isFacingBack = false
	static height = 0.3
	static fabricTexture: BABYLON.Texture
	static top: BABYLON.AbstractMesh
	static overrides: BABYLON.AnimationPropertiesOverride
	static setup(engine: BABYLON.Engine) {
		Player.engine = engine
		Player.scene = Player.engine.scenes[0]
		Player.skeleton = Player.scene.meshes[0].skeleton!
		Player.root = Player.scene.getMeshByName("body")
		Player.scene.getMeshByName("eyes")!.parent = Player.root
		Player.scene.getMeshByName("tongue")!.parent = Player.root
		Player.top = Player.scene.getMeshByName("top")!
		Player.top.parent = Player.root
		Player.mesh = Player.scene.getMeshByName("body")
		Player.fabric = Player.scene.getMaterialByName(
			"matFabric"
		)! as BABYLON.PBRMaterial
		Player.hems = Player.scene.getMaterialByName(
			"matHems"
		)! as BABYLON.PBRMaterial
		Player.overrides = new BABYLON.AnimationPropertiesOverride()
		Player.fabricTexture = (
			Player.scene.getMaterialByName("matFabric")! as BABYLON.PBRMaterial
		).albedoTexture as BABYLON.Texture
		Player.fabricTexture.wAng = Math.PI / 2
		Player.setBlendingSpeed(0.1)
		Player.skeleton.beginAnimation("Idle", true, 1)
		Input.player = Player
	}

	static setBlendingSpeed(speed: number) {
		if (speed !== Player.overrides.blendingSpeed) {
			Player.overrides.enableBlending = true
			Player.overrides.blendingSpeed = speed
			Player.skeleton.animationPropertiesOverride = Player.overrides
		}
	}

	static move(isForward: boolean, speed: number) {
		var xMultiplier
		var zMultiplier

		if (isForward) {
			xMultiplier = 1
			zMultiplier = -1
		} else {
			xMultiplier = -1
			zMultiplier = 1
		}

		const delta = Player.engine.getDeltaTime()
		const spd = Math.abs(speed)
		if (!Player.mesh) return
		const cosRot = Math.cos(Player.absoluteRotation)
		const sinRot = Math.sin(Player.absoluteRotation)
		var forwards = new BABYLON.Vector3(
			xMultiplier * spd * delta * Player.interval * cosRot,
			0,
			zMultiplier * spd * Player.interval * delta * sinRot
		)
		Player.root!.moveWithCollisions(forwards)
		Player.roundPosition()
	}

	static fall() {
		const delta = Player.engine.getDeltaTime()
		Player.velocity.y += (Player.scene.gravity.y / 1000) * delta
		Player.root!.moveWithCollisions(Player.velocity)
		Player.roundPosition()
	}

	static setFabric(filename: string) {
		Player.fabricTexture = new BABYLON.Texture(filename, Player.scene)
		Player.fabric.albedoTexture = Player.fabricTexture
	}

	static setHems(hexColour: string) {
		Player.hems.albedoColor = BABYLON.Color3.FromHexString(hexColour)
	}

	static setShine(value: number) {
		const roughness = 1 - value / 100
		const metallic = value / 100
		Player.hems.metallic = metallic
		Player.hems.roughness = roughness
	}

	static rotateFabric(degrees: number) {
		Player.fabricTexture.wAng = BABYLON.Tools.ToRadians(degrees)
	}

	static rotate(direction = true) {
		var y
		if (Input.joystickLastDeltaX !== 0) {
			y = Player.speed.rotate * 50 * Math.abs(Input.joystickLastDeltaX)
		} else {
			y = Player.speed.rotate * 50
		}
		let multiplier = 1
		if (this.isFacingBack) multiplier = -1
		if (direction) {
			Player.absoluteRotation += y * multiplier
		} else {
			Player.absoluteRotation -= y * multiplier
		}
		Player.root!.rotation.y =
			Player.absoluteRotation + multiplier * Math.PI * 1.5
		Player.roundRotation()
	}

	static roundPosition() {
		if (!!Player.root) {
			Player.root.position.x = Math.round(1000 * Player.root.position.x) / 1000
			Player.root.position.z = Math.round(1000 * Player.root.position.z) / 1000
		}
	}

	static roundRotation() {
		Player.absoluteRotation = Math.round(1000 * Player.absoluteRotation) / 1000
	}

	static toggleTop(toShow: boolean) {
		Player.top.isVisible = toShow
	}

	static centre() {
		Player.root!.position = BABYLON.Vector3.Zero()
		this.isFacingBack = false
		Player.absoluteRotation = 0
		Player.root!.rotation.y = Player.absoluteRotation + Math.PI * 1.5
	}

	static xrPositon(xr?: BABYLON.WebXRDefaultExperience) {
		Player.root!.position = new BABYLON.Vector3(0, 0.9, 1)
		Player.scene.getMeshByName("body")!.isPickable = true
		this.isFacingBack = false
		Player.absoluteRotation = 0
		Player.root!.isPickable = true

		if (xr != null) {
			xr!.teleportation.detach()
		}
	}
	static lastAngleDiff = { y: 0 }
	static framecount = 0
	static mxframecount = 120 // Maximum frames for inertia effect

	static xrInteraction(
		mesh: BABYLON.Nullable<BABYLON.AbstractMesh>,
		pickedPoint: BABYLON.Vector3
	) {
		if (!mesh || mesh.name !== Player.root!.name) {
			return // Early return if mesh is null or not the player
		}

		if (Player.root!) {
			const oldAngle = Player.root!.rotation.y
			const rotationAdjustmentFactor = 0.1 // Adjust for sensitivity
			Player.root!.rotation.y -=
				rotationAdjustmentFactor * (pickedPoint.x - Player.root!.position.x)

			// Calculate angle difference for inertia
			const newAngle = Player.root!.rotation.y
			this.lastAngleDiff.y = newAngle - oldAngle

			// Adjust position slightly below the picked point
			Player.root!.position.y = pickedPoint.y - 0.25

			// Manage input and idle timeout
			clearTimeout(Input.idleTimeout)
			Input.idleTimeout = setTimeout(() => Input.idle(), 7000)
			Input.key.idle = false
		}
	}

	static applyInertia() {
		if (this.framecount < this.mxframecount) {
			// Slow down the rotation each frame
			this.lastAngleDiff.y /= 1.1

			// Apply the decreased rotation
			Player.root!.rotation.y += this.lastAngleDiff.y
			this.framecount++
		} else {
			this.framecount = 0 // Reset after reaching the max frame count
		}
	}

	static calculateMove() {
		try {
			//Moving forward
			if (Input.key.up || Input.key.jup) {
				if (!Input.key.jup) {
					Player.viewingRotation -=
						(Player.viewingRotation - Player.absoluteRotation) /
						Player.smoothingFactor
					if (Player.isFacingBack) {
						//Spin avatar around if facing back
						Player.root!.rotation.y += Math.PI
						Player.isFacingBack = false
					}
				}
				if (Input.key.run) {
					if (Input.state !== Motion.RUNNING) {
						Player.setBlendingSpeed(0.1)
						Player.skeleton.beginAnimation("Run", true, 1)
						Input.state = Motion.RUNNING
					}
					Player.move(true, this.speed.run)
				} else if (Input.key.slow) {
					if (Input.state !== Motion.SLOWING) {
						Player.setBlendingSpeed(0.1)
						Player.skeleton.beginAnimation("Walk", true, 1)
						Input.state = Motion.SLOWING
					}
					Player.move(true, this.speed.slow)
				} else {
					if (Input.state !== Motion.WALKING) {
						Player.isMoving = true
						Player.setBlendingSpeed(0.1)
						Player.skeleton.beginAnimation("Trot", true, 1)
						Input.state = Motion.WALKING
					}
					Player.move(true, Player.speed.walk)
				}
				//Moving backwards
			} else if (Input.key.down) {
				Player.viewingRotation -=
					(Player.viewingRotation - Player.absoluteRotation) /
					Player.smoothingFactor
				//Spin avatar back if facing forward
				if (!this.isFacingBack) {
					Player.root!.rotation.y += Math.PI
					this.isFacingBack = true
				}
				//Running back
				if (Input.key.run) {
					if (Input.state !== Motion.RUNNING) {
						Player.setBlendingSpeed(0.1)
						Player.skeleton.beginAnimation("Run", true, 1)
						Input.state = Motion.RUNNING
					}
					Player.move(false, Player.speed.run)
					//Walking back
				} else if (Input.key.slow) {
					if (Input.state !== Motion.SLOWING) {
						Player.setBlendingSpeed(0.1)
						Player.skeleton.beginAnimation("Walk", true, 1)
						Input.state = Motion.SLOWING
					}
					Player.move(false, this.speed.slow)
				} else {
					if (Input.state !== Motion.WALKING) {
						Player.setBlendingSpeed(0.1)
						Player.skeleton.beginAnimation("Trot", true, 1)
						Input.state = Motion.WALKING
					}
					Player.move(false, Player.speed.walk)
				}
			} else if (Input.key.idle) {
				if (Input.state !== Motion.SITTING) {
					Player.setBlendingSpeed(0.01)
					Player.skeleton.beginAnimation("Sit", true, 1)
					Input.state = Motion.SITTING
				}
				//In place
			} else if (Input.state !== Motion.IDLE) {
				if (Input.key.left) {
					//If we just stopped moving and LEFT key is down
					Player.rotate(false)
					if (Input.state !== Motion.TURNINGLEFT) {
						Player.isMoving = true
						Player.setBlendingSpeed(0.1)
						Player.skeleton.beginAnimation("Turn_left", true, 1)
						Input.state = Motion.TURNINGLEFT
					}
					//If we just stopped moving and RIGHT key is down
				} else if (Input.key.right) {
					Player.rotate()
					if (Input.state !== Motion.TURNINGRIGHT) {
						Player.isMoving = true
						Player.setBlendingSpeed(0.1)
						Player.skeleton.beginAnimation("Turn_right", true, 1)
						Input.state = Motion.TURNINGRIGHT
					}
				} else {
					Player.scene.stopAllAnimations()
					Player.setBlendingSpeed(0.1)
					Player.skeleton.beginAnimation("Idle", true, 1)
					Input.state = Motion.IDLE
				}
			}
			if (Input.key.left) {
				//Turning left on the spot
				Player.rotate(false)
				if (Input.state === Motion.IDLE) {
					Player.isMoving = true
					Player.skeleton.beginAnimation("Turn_left", true, 1)
					Input.state = Motion.TURNINGLEFT
				} else if (
					Input.state === Motion.WALKING ||
					Input.state === Motion.RUNNING
				) {
					Player.isMoving = true
				}
			} else if (Input.key.right) {
				//Turning right on the spot
				Player.rotate()
				if (Input.state === Motion.IDLE) {
					Player.isMoving = true
					Player.setBlendingSpeed(0.1)
					Player.skeleton.beginAnimation("Turn_right", true, 1)
					Input.state = Motion.TURNINGRIGHT
				} else if (
					Input.state === Motion.WALKING ||
					Input.state === Motion.RUNNING
				) {
					Player.isMoving = true
				}
			}
		} catch (err) {
			//Ignore the error, most likely due to Avatar mesh disposed when trying to access it
			console.log(err)
		}
	}

	static nonsteeringMove() {
		try {
			const adjusted = Input.angle - Math.PI * 1.5
			Player.root!.rotation.y = adjusted
			Player.absoluteRotation = Input.angle + Math.PI
			this.isFacingBack = false
		} catch (err) {
			//Ignore the error, most likely due to Avatar mesh disposed when trying to access it
			console.log(err)
		}
	}

	static update() {
		if (!Input.keysEnabled && !Input.key.idle && Input.state !== Motion.SITTING)
			return
		if (!Player.root) return
		Player.calculateMove()
		if (Input.key.jup && Input.distance > 20) Player.nonsteeringMove()
	}
}
